import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ReactTooltip from 'react-tooltip';

import Select from '../../../../Select';
import { FONT_FAMILY, FONT_STYLE, TEXT_ALIGN, TEXT_TRANSFORM } from './helpers';
import { capitalize } from '../../../../../../util/text';
import CardDesignerCardElementColorControl from '../CardDesignerCardElementControls/components/CardDesignerCardElementColorControl';
import Button from '../../../../Button';
import BoldIcon from '../../../../../icons/BoldIcon';
import ItalicIcon from '../../../../../icons/ItalicIcon';
import UppercaseIcon from '../../../../../icons/UppercaseIcon';

const propTypes = {
  element: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
  }).isRequired,
  onElementConfigurationChange: PropTypes.func.isRequired,
  setLastUsedFont: PropTypes.func.isRequired,
};

const CardDesignerCardElementTextControls = ({
  element,
  onElementConfigurationChange,
  setLastUsedFont,
}) => {
  const { cardElementConfiguration = {} } = element;

  return (
    <Fragment>
      <Select
        id="font-family"
        size="sm"
        value={cardElementConfiguration.font_family}
        onChange={event => {
          const { value } = event.target;
          onElementConfigurationChange({
            element,
            elementConfig: {
              font_family: value,
            },
          });
          setLastUsedFont(value);
        }}
      >
        {Object.values(FONT_FAMILY).map(fontFamily => {
          return (
            <option key={fontFamily} value={fontFamily}>
              {fontFamily
                .replace('GOOGLE_', '')
                .split('_')
                .map(s => capitalize(s))
                .join(' ')}
            </option>
          );
        })}
      </Select>

      <CardDesignerCardElementColorControl
        className="ml-2"
        onChange={hex => {
          onElementConfigurationChange({
            element,
            elementConfig: {
              font_color: hex,
            },
          });
        }}
        defaultColor={cardElementConfiguration.font_color ?? undefined}
      />

      <Button
        btnStyle="white"
        className={classNames({
          'ml-2 card-designer-card-element-control-btn': true,
          'is-active': cardElementConfiguration.font_weight === 'bold',
        })}
        onClick={() => {
          onElementConfigurationChange({
            element,
            elementConfig: {
              font_weight:
                cardElementConfiguration.font_weight === 'bold' ? null : 'bold',
            },
          });
        }}
      >
        <BoldIcon />
      </Button>

      <Button
        btnStyle="white"
        className={classNames({
          'ml-2 card-designer-card-element-control-btn': true,
          'is-active':
            cardElementConfiguration.font_style === FONT_STYLE.ITALIC,
        })}
        onClick={() => {
          onElementConfigurationChange({
            element,
            elementConfig: {
              font_style:
                cardElementConfiguration.font_style === FONT_STYLE.ITALIC
                  ? FONT_STYLE.NORMAL
                  : FONT_STYLE.ITALIC,
            },
          });
        }}
      >
        <ItalicIcon />
      </Button>

      <Button
        btnStyle="white"
        className={classNames({
          'ml-2 card-designer-card-element-control-btn': true,
          'is-active':
            cardElementConfiguration.text_transform ===
            TEXT_TRANSFORM.UPPERCASE,
        })}
        onClick={() => {
          onElementConfigurationChange({
            element,
            elementConfig: {
              text_transform:
                cardElementConfiguration.text_transform ===
                TEXT_TRANSFORM.UPPERCASE
                  ? TEXT_TRANSFORM.NONE
                  : TEXT_TRANSFORM.UPPERCASE,
            },
          });
        }}
        data-for="uppercase-button"
        data-place="bottom"
        data-effect="solid"
        data-tip={
          cardElementConfiguration.text_transform === TEXT_TRANSFORM.UPPERCASE
            ? 'Lowercase'
            : 'Uppercase'
        }
      >
        <UppercaseIcon />
        <ReactTooltip id="uppercase-button" className="tooltip" />
      </Button>

      <Select
        id="text-align"
        size="sm"
        className="ml-2"
        value={cardElementConfiguration.text_align || TEXT_ALIGN.LEFT}
        onChange={event => {
          const { value } = event.target;
          onElementConfigurationChange({
            element,
            elementConfig: {
              text_align: value,
            },
          });
        }}
      >
        {Object.values(TEXT_ALIGN).map(textAlign => {
          return (
            <option key={textAlign} value={textAlign}>
              {capitalize(textAlign)}
            </option>
          );
        })}
      </Select>
    </Fragment>
  );
};

CardDesignerCardElementTextControls.propTypes = propTypes;

export default CardDesignerCardElementTextControls;
